import { Link } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import client from "@/lib/api";
import { ArrowRight, Crown, Factory, Swords, Globe2, Compass, Sword, Briefcase, Coins, Landmark, MessageCircle, MessageSquare, Megaphone, Headphones, Sparkles } from "lucide-react";
import DevShowcase from "@/components/DevShowcase";
import HeroGlobe from "@/components/HeroGlobe";
import BetaCountdown from "@/components/BetaCountdown";
import RoadmapTeaser from "@/components/RoadmapTeaser";
import { groupVisibleSections, CategoryTree } from "@/components/WikiCategoryTree";

export default function Home() {
  const { t, i18n } = useTranslation();
  const [pages, setPages] = useState([]);

  useEffect(() => {
    client.get("/wiki/pages", { params: { language: i18n.language } }).then(r => setPages(r.data)).catch(() => {});
  }, [i18n.language]);

  const wikiSections = useMemo(() => groupVisibleSections(pages), [pages]);

  const PATHS = useMemo(() => [
    { slug: "soldier",      icon: Sword,    name: t("home.pathSoldierName"),      tagline: t("home.pathSoldierTagline"),      blurb: t("home.pathSoldierBlurb"),      to: "/forum?category=strategy" },
    { slug: "entrepreneur", icon: Briefcase, name: t("home.pathEntrepreneurName"), tagline: t("home.pathEntrepreneurTagline"), blurb: t("home.pathEntrepreneurBlurb"), to: "/forum?category=suggestions" },
    { slug: "trader",       icon: Coins,    name: t("home.pathTraderName"),       tagline: t("home.pathTraderTagline"),       blurb: t("home.pathTraderBlurb"),       to: "/forum?category=economy-trade" },
    { slug: "politician",   icon: Landmark, name: t("home.pathPoliticianName"),   tagline: t("home.pathPoliticianTagline"),   blurb: t("home.pathPoliticianBlurb"),   to: "/forum?category=diplomacy" },
  ], [t]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] md:w-[900px] md:h-[900px] rounded-full bg-[radial-gradient(circle,_rgba(58,160,255,0.18)_0%,_transparent_60%)]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 pt-10 md:pt-20 pb-14 md:pb-24 grid lg:grid-cols-[1.1fr_0.9fr] gap-8 md:gap-12 items-center">
          <div className="order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-[var(--t-border-glow)] mb-5 md:mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--t-blue)] pulse-glow" />
              <span className="font-mono text-[10px] md:text-[11px] tracking-[0.25em] md:tracking-[0.3em] text-[var(--t-cyan)] uppercase">{t("home.heroEyebrow")}</span>
            </div>
            <h1 className="font-display text-[2.25rem] leading-[1.04] sm:text-5xl md:text-6xl lg:text-7xl mb-5 md:mb-6">
              <span className="block bg-gradient-to-r from-[var(--t-blue-glow)] via-[var(--t-cyan)] to-[var(--t-blue)] bg-clip-text text-transparent">{t("home.heroLine1")}</span>
              <span className="block text-[var(--t-text)]">{t("home.heroLine2")}</span>
              <span className="block text-[var(--t-text)]">{t("home.heroLine3")}</span>
            </h1>
            <p className="text-base md:text-lg text-[var(--t-text-dim)] max-w-xl mb-6 md:mb-8 leading-relaxed">
              {t("home.heroBlurb")}
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="https://territoria.world" target="_blank" rel="noreferrer" className="btn-primary flex-1 sm:flex-initial !justify-center" data-testid="cta-enter-world">
                {t("home.ctaEnter")} <ArrowRight className="w-4 h-4" />
              </a>
              <Link to="/wiki" className="btn-ghost flex-1 sm:flex-initial !justify-center" data-testid="cta-explore-codex">
                <Globe2 className="w-4 h-4" /> {t("home.ctaExplore")}
              </Link>
              <Link to="/discord" className="btn-ghost flex-1 sm:flex-initial !justify-center !border-[#5865F2] !text-[#9ca6ff] hover:!bg-[rgba(88,101,242,0.12)] hover:!text-[#c5cbff] hover:!border-[#7c87ff]" data-testid="cta-discord">
                <MessageCircle className="w-4 h-4" /> {t("home.ctaDiscord")}
              </Link>
            </div>
          </div>

          <div className="order-1 lg:order-2 relative flex justify-center">
            <HeroGlobe />
          </div>
        </div>
      </section>

      {/* OPEN BETA COUNTDOWN — full width */}
      <section className="relative max-w-7xl mx-auto px-4 md:px-6 -mt-2" data-testid="hero-beta-countdown-wrap">
        <BetaCountdown />
      </section>

      {/* JOIN THE PIONEERS (Dev Showcase) */}
      <DevShowcase />

      {/* DEVELOPMENT ROADMAP TEASER */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-10 md:pt-14 pb-0">
        <div className="inline-flex items-center gap-2 px-3 py-1 border border-[var(--t-border-glow)]" data-testid="dev-build-tag">
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--t-gold)] pulse-glow" />
          <span className="font-mono text-[10px] md:text-[11px] tracking-[0.3em] text-[var(--t-gold)] uppercase">{t("home.devBuildTag")}</span>
        </div>
      </div>
      <RoadmapTeaser />

      {/* JOIN THE COMMUNITY */}
      <section className="relative max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-12" data-testid="join-community">
        <div className="panel panel-glow corner-frame p-6 md:p-10 lg:p-12 relative overflow-hidden">
          <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-[radial-gradient(circle,_rgba(88,101,242,0.32)_0%,_transparent_70%)] pointer-events-none" />
          <div className="absolute -left-32 -bottom-32 w-80 h-80 rounded-full bg-[radial-gradient(circle,_rgba(58,160,255,0.18)_0%,_transparent_70%)] pointer-events-none" />

          <div className="relative grid md:grid-cols-[1.4fr_1fr] gap-7 md:gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#5865F2] bg-[rgba(88,101,242,0.08)] mb-4 md:mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#5865F2] pulse-glow" />
                <span className="font-mono text-[10px] md:text-[11px] tracking-[0.25em] md:tracking-[0.3em] text-[#9ca6ff] uppercase">{t("home.communityTag")}</span>
              </div>
              <h2 className="font-display text-2xl md:text-4xl text-[var(--t-text)] uppercase leading-tight">
                {t("home.communityTitle")}
              </h2>
              <p className="mt-3 md:mt-4 text-sm md:text-base text-[var(--t-text-dim)] max-w-xl leading-relaxed">
                {t("home.communityBlurb")}
              </p>

              <ul className="mt-5 md:mt-6 grid sm:grid-cols-2 gap-2 max-w-xl">
                {[
                  [t("home.communityBullet1"), Megaphone],
                  [t("home.communityBullet2"), Sparkles],
                  [t("home.communityBullet3"), MessageSquare],
                  [t("home.communityBullet4"), Headphones],
                ].map(([label, Icon]) => (
                  <li key={label} className="flex items-center gap-2.5 text-sm text-[var(--t-text-dim)]">
                    <Icon className="w-4 h-4 text-[#9ca6ff] flex-shrink-0" strokeWidth={1.6} />
                    <span>{label}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col gap-3">
              <a
                href="https://discord.gg/territoria"
                target="_blank"
                rel="noreferrer"
                className="relative inline-flex items-center justify-center gap-2 px-5 py-3.5 md:py-4 font-display tracking-[0.12em] uppercase text-sm md:text-base text-white border transition group"
                style={{
                  background: "linear-gradient(180deg, #5865F2 0%, #404eed 100%)",
                  borderColor: "#7c87ff",
                  clipPath: "polygon(10px 0, calc(100% - 10px) 0, 100% 10px, 100% calc(100% - 10px), calc(100% - 10px) 100%, 10px 100%, 0 calc(100% - 10px), 0 10px)",
                  boxShadow: "0 0 0 1px rgba(124,135,255,0.5), inset 0 1px 0 rgba(255,255,255,0.18), 0 10px 32px rgba(88,101,242,0.45)",
                }}
                data-testid="join-discord-btn"
              >
                <MessageCircle className="w-5 h-5" />
                {t("home.joinDiscord")}
                <ArrowRight className="w-4 h-4 transition group-hover:translate-x-0.5" />
              </a>
              <Link
                to="/discord"
                className="btn-ghost !justify-center !text-[11px] md:!text-xs"
                data-testid="join-discord-info-btn"
              >
                <Globe2 className="w-4 h-4" /> {t("home.learnMore")}
              </Link>
              <div className="text-center mt-1 font-mono text-[10px] md:text-[11px] tracking-[0.18em] text-[var(--t-text-muted)] uppercase">
                {t("home.discordCaption")}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PATHS */}
      <section className="relative max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-12">
        <div className="mb-6 md:mb-8">
          <span className="font-mono text-[10px] md:text-xs tracking-[0.25em] md:tracking-[0.3em] text-[var(--t-blue-glow)] uppercase">{t("home.buildsEyebrow")}</span>
          <h2 className="font-display text-2xl md:text-3xl mt-2 text-[var(--t-text)] uppercase">{t("home.buildsTitle")}</h2>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4" data-testid="paths-grid">
          {PATHS.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.slug}
                className="panel panel-glow corner-frame p-4 md:p-6 flex flex-col"
                data-testid={`path-${p.slug}`}
              >
                <Icon className="w-7 h-7 md:w-8 md:h-8 text-[var(--t-blue-glow)] mb-3 md:mb-4" />
                <div className="font-display text-base md:text-xl tracking-[0.08em] text-[var(--t-text)] uppercase mb-1 leading-tight">{p.name}</div>
                <div className="font-mono text-[10px] text-[var(--t-cyan)] tracking-[0.22em] uppercase mb-2.5">{p.tagline}</div>
                <p className="text-xs md:text-sm text-[var(--t-text-dim)] leading-relaxed">{p.blurb}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* WIKI */}
      <section className="relative max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-12">
        <div className="flex items-end justify-between mb-6 md:mb-8 gap-4">
          <div>
            <span className="font-mono text-[10px] md:text-xs tracking-[0.25em] md:tracking-[0.3em] text-[var(--t-blue-glow)] uppercase">{t("home.wikiEyebrow")}</span>
            <h2 className="font-display text-2xl md:text-3xl mt-2 text-[var(--t-text)] uppercase">{t("home.wikiTitle")}</h2>
          </div>
          <Link to="/wiki" className="btn-ghost !py-2 !px-3 md:!px-4 !text-[10px] md:!text-xs flex-shrink-0" data-testid="all-pages-link">{t("home.wikiAll")} <ArrowRight className="w-3 h-3" /></Link>
        </div>
        <div className="space-y-6 md:space-y-8" data-testid="home-wiki-sections">
          {wikiSections.map(([cat, items]) => (
            <CategoryTree key={cat} category={cat} entries={items} />
          ))}
        </div>
      </section>
    </div>
  );
}
